import { ReactNode } from "react";
import { LoggedInUserStore } from "../loggedInUser/LoggedInUserStore";
import { AngularBridge } from "../bridgeSetup/AngularBridge";
import { TabbedRoutesPageWrapper } from "../../uiLibrary/tabbedRoutesPageWrapper/TabbedRoutesPageWrapper";
import { LegacyGlobal } from "./LegacyGlobal";
import * as overrides from "./LegacyGlobal";
import { SettingsPage } from "../../features/settings/SettingsPage";
import { AdminPage } from "../../features/admin/AdminPage";
import { UserPage } from "../../features/user/UserPage";
import { HomePage } from "../../features/home/HomePage";
import { StopImpersonatingPage } from "../../features/user/StopImpersonatingPage";
import { DemoConsolePage } from "../../features/demoConsole/DemoConsolePage";
import { ManageInteractionsPageWrapper } from "../../features/manageInteractions/ManageInteractionsPageWrapper";
import { TrackConsumptionPage } from "../../features/trackConsumption/TrackConsumptionPage";
import { LazyRedBoxPlayerBridge } from "../../features/redBoxVoiceCall/LazyPlayerBridge";
import { LazyMicrosoftTeamsCallPlayerBridge } from "../../features/microsoftTeamsCall/LazyPlayerBridge";
import { LazyTestAreaPage } from "../../features/testArea/LazyTestAreaPage";
import { LazySystemMonitorPage } from "../../features/systemMonitor/LazySystemMonitorPage";
import { LazySlackConnectedSurveillanceAppPage } from "../../features/slackConnectedSurveillanceApp/LazySlackConnectedSurveillanceAppPage";
import { LazyMicrosoftGraphConnectedSurveillanceAppPage } from "../../features/connectedSurveillance/microsoftGraphConnectedSurveillanceApp/LazyMicrosoftGraphConnectedSurveillanceAppPage";
import { LazyMicrosoftGraphExchangeConnectedSurveillanceAppPage } from "../../features/connectedSurveillance/microsoftGraphExchangeConnectedSurveillanceApp/LazyMicrosoftGraphExchangeConnectedSurveillanceAppPage";
import { LazyClientAgentConnectedSurveillanceAppPage } from "../../features/connectedSurveillance/clientAgentConnectedSurveillanceApp/LazyClientAgentConnectedSurveillanceAppPage";
import { FrontOfficePageWrapper } from "../../features/manageInteractions/FrontOfficePageWrapper";
import { ILink } from "./useLink";
import {
    LazyInfiniteTableExamplePage,
    LazyTableExamplePage
} from "../../uiLibrary/table/__examples__/LazyTableExamplePages";
import { LazyVirtualScrollExamplePage } from "../../uiLibrary/virtualScroll/__examples__/LazyVirtualScrollExamplePage";
import { LazyReviewInducementScan } from "../../features/monitorInducement/LazyReviewInducementScan";
import { LazyDemoDataPage } from "../../features/demoData/LazyDemoDataPage";
import { LazyLlmPromptTemplatePage } from "../../features/llm/LazyLlmPromptTemplatePage";
import { LazyLexiconPage } from "../../features/lexicons/LazyLexiconPage";
import { LazyUnusualInstrumentsPage } from "../../features/unusualInstruments/LazyUnusualInstrumentsPage";
import { LazyWormCallPlayer } from "../../features/wormCall/LazyWormCallPlayer";
import { SearchPage } from "../../features/feed/SearchPage";
import { LazyRulePage } from "../../features/rules/LazyRulePage";
import { LazyCaseTagPage } from "@/features/caseTags/LazyCaseTagPage";
import { LazyBackTestRunsPage } from "../../features/backTestRuns/LazyBackTestRunsPage";
import { LazyAdminToolsPage } from "@/features/adminTools/LazyAdminToolsPage";
import { InternalMediaArticlePage } from "../../features/internalMedia/InternalMediaArticlePage";
import { InternalMediaListPage } from "../../features/internalMedia/InternalMediaListPage";

interface IAccess {
    grantAccess?: boolean;
    overlay?: ReactNode;
}

interface IRender {
    params: Record<string, string>;
    search: string;
    loggedInUserStore: LoggedInUserStore;
    refresh: () => void;
}

export interface IRoute {
    isPermissoned?: (loggedInUserStore: LoggedInUserStore) => boolean;
    onlyShowIfThereArePermissionedChildren?: boolean;
    shouldAllowAccess?: (
        loggedInUserStore: LoggedInUserStore,
        path: string | undefined,
        target: string | undefined,
        dismissOverlay: (grantAccess: boolean) => void
    ) => IAccess;
    title?: string | ((loggedInUserStore: LoggedInUserStore) => string);
    description?: string | ((loggedInUserStore: LoggedInUserStore) => string);
    path?: string;
    pathExact?: boolean;
    defaultPathParams?: unknown;
    useFirstChildForMenus?: boolean;
    spreadChildrenForMenus?: boolean;
    render?: (config: IRender) => ReactNode;
    useNavigationLink?: () => ILink;
    isPrintableView?: boolean;
    wrapChildrenRender?: (children: ReactNode) => ReactNode;
    children?: IRoute[];
}

export const communityArticleRoute: IRoute = {
    path: "/Community/:id",
    render: ({ params }) => <InternalMediaArticlePage id={params["id"] ?? ""} />
};

export const communityRoute: IRoute = {
    isPermissoned: x => x.splits?.BettermodeVisible ?? false,
    title: "Community",
    description: "Community articles and posts",
    path: "/Community",
    pathExact: true,
    render: () => <InternalMediaListPage />,
    children: [communityArticleRoute]
};

export const mainNavRoutes: IRoute[] = [
    {
        isPermissoned: x =>
            x.hasPermission(
                RedDeer.Contracts.Enums.PermissionType.CanAccessMifid
            ) && x.evaluationIsActive,
        title: "Research evaluation",
        description: "Submit evaluation for research providers",
        path: "/Evaluation",
        render: () => (
            <AngularBridge
                name="c-mifid-research-vote"
                componentClassName="o-page"
            />
        )
    },

    {
        isPermissoned: x =>
            x.hasPermission(
                RedDeer.Contracts.Enums.PermissionType.CanAccessSurveillance
            ),
        title: "Investigate cases",
        description:
            "Review, close or escalate cases of potential rule breaches",
        path: "/CasesHome",
        render: () => <AngularBridge name="c-mar-overview" />,
        children: [
            {
                path: "/Case/:id",
                render: ({ params, refresh }) => (
                    <AngularBridge
                        name="c-mar-case"
                        props={{
                            componentKey: "casePage",
                            caseId: params["id"],
                            refreshPage: refresh
                        }}
                    />
                )
            }
        ]
    },

    {
        isPermissoned: x =>
            x.hasPermission(
                RedDeer.Contracts.Enums.PermissionType.CanAccessSurveillance
            ) ||
            x.hasPermission(
                RedDeer.Contracts.Enums.PermissionType.CanAccessMifid
            ),
        title: x =>
            x.hasPermission(
                RedDeer.Contracts.Enums.PermissionType.CanAccessMifid
            )
                ? "Catch up"
                : "Search data",
        description: x =>
            x.hasPermission(
                RedDeer.Contracts.Enums.PermissionType.CanAccessSurveillance
            )
                ? "Search your data for ad-hoc investigations"
                : "Search for content",
        path: "/Search",
        render: ({ search }) => <SearchPage search={search} />,
        children: [
            {
                title: "Voice call player",
                path: "/RedBoxVoiceCallPlayer/:id",
                isPrintableView: true,
                render: ({ params }) => (
                    <LazyRedBoxPlayerBridge id={params["id"] ?? ""} />
                )
            },
            {
                title: "Video call player",
                path: "/MicrosoftTeamsVideoCallPlayer/:id",
                isPrintableView: true,
                render: ({ params }) => (
                    <LazyMicrosoftTeamsCallPlayerBridge
                        id={params["id"] ?? ""}
                    />
                )
            },
            {
                title: "Audio call player",
                path: "/MicrosoftTeamsAudioCallPlayer/:id",
                isPrintableView: true,
                render: ({ params }) => (
                    <LazyMicrosoftTeamsCallPlayerBridge
                        id={params["id"] ?? ""}
                    />
                )
            },
            {
                title: "Voice call player",
                path: "/WormCallPlayer/:id",
                isPrintableView: true,
                render: ({ params }) => (
                    <LazyWormCallPlayer id={params["id"] ?? ""} />
                )
            }
        ]
    },
    {
        isPermissoned: x =>
            x.hasPermission(
                RedDeer.Contracts.Enums.PermissionType.CanAccessSurveillance
            ),
        title: "Manage policies",
        description:
            "Create MAR policies and attach and calibrate surveillance rules",
        path: "/PoliciesHome/",
        render: () => (
            <AngularBridge
                name="c-mar-policies-home-page"
                props={{ componentKey: "app" }}
            />
        ),
        children: [
            {
                path: "/Policy/:id",
                render: ({ params, refresh }) => (
                    <AngularBridge
                        name="c-mar-policy-page"
                        props={{
                            componentKey: "app",
                            params,
                            refreshPage: refresh
                        }}
                    />
                )
            },
            {
                path: "/Rule/Create/:policyId",
                render: ({ params }) => {
                    const policyId = params["policyId"];
                    return <LazyRulePage policyId={policyId ?? ""} />;
                }
            },
            {
                path: "/Rule/Edit/:ruleId",
                render: ({ params }) => {
                    const ruleId = params["ruleId"];
                    return <LazyRulePage ruleId={ruleId ?? ""} />;
                }
            },
            {
                path: "/Backtest/Create/:policyId",
                render: ({ params }) => {
                    const policyId = params["policyId"];
                    return <LazyBackTestRunsPage policyId={policyId ?? ""} />;
                }
            },
            {
                path: "/Backtest/Edit/:policyId/:ideaDataItemId/:tab?/:backtestRunId?",
                pathExact: false,
                render: ({ params }) => {
                    const ideaDataItemId = params["ideaDataItemId"];
                    const policyId = params["policyId"];
                    const backtestRunId = params["backtestRunId"];
                    const tab = params["tab"];
                    return (
                        <LazyBackTestRunsPage
                            ideaDataItemId={ideaDataItemId ?? ""}
                            policyId={policyId ?? ""}
                            backtestRunId={backtestRunId}
                            tab={tab}
                        />
                    );
                }
            }
        ]
    },
    {
        onlyShowIfThereArePermissionedChildren: true,
        title: "Manage interactions",
        description: "Review and manage research interactions",
        useFirstChildForMenus: true,
        wrapChildrenRender: children => (
            <ManageInteractionsPageWrapper>
                {children}
            </ManageInteractionsPageWrapper>
        ),
        children: [
            {
                isPermissoned: x =>
                    x.hasPermission(
                        RedDeer.Contracts.Enums.PermissionType.CanAccessMifid
                    ) &&
                    x.enableInteractionReview &&
                    !x.isMiddleOfficeUser,
                spreadChildrenForMenus: true,
                children: [
                    {
                        title: "Review",
                        path: "/InteractionReview",
                        pathExact: true,
                        render: () => (
                            <FrontOfficePageWrapper view="review">
                                <AngularBridge
                                    name="c-interaction-review-review"
                                    props={{
                                        componentKey: "interactionReview"
                                    }}
                                    componentClassName="h-fill"
                                />
                            </FrontOfficePageWrapper>
                        )
                    },
                    {
                        title: "Summary",
                        path: "/InteractionReview/Summary",
                        pathExact: true,
                        render: () => (
                            <FrontOfficePageWrapper view="summary">
                                <AngularBridge
                                    name="c-interaction-review-summary"
                                    props={{
                                        componentKey: "interactionReview"
                                    }}
                                    componentClassName="h-fill"
                                />
                            </FrontOfficePageWrapper>
                        )
                    }
                ]
            },
            {
                isPermissoned: x =>
                    x.hasPermission(
                        RedDeer.Contracts.Enums.PermissionType.CanAccessMifid
                    ) &&
                    x.enableInteractionReview &&
                    x.isMiddleOfficeUser,
                spreadChildrenForMenus: true,
                children: [
                    {
                        title: "View progress",
                        path: "/InteractionReview",
                        pathExact: true,
                        render: () => (
                            <AngularBridge
                                name="c-interaction-review-middle-office-dashboard"
                                props={{
                                    componentKey: "interactionReview"
                                }}
                                componentClassName="h-fill"
                            />
                        )
                    },
                    {
                        title: "Check submissions",
                        path: "/InteractionReview/Submissions",
                        render: () => (
                            <AngularBridge
                                name="c-interaction-review-middle-office-submissions"
                                props={{
                                    componentKey: "interactionReview"
                                }}
                                componentClassName="h-fill"
                            />
                        )
                    },
                    {
                        title: "Monitor front office",
                        path: "/InteractionReview/MonitorFrontOffice",
                        render: () => (
                            <AngularBridge
                                name="c-interaction-review-middle-office-under-review"
                                props={{
                                    componentKey: "interactionReview"
                                }}
                                componentClassName="h-fill"
                            />
                        )
                    },
                    {
                        title: "Processed interactions",
                        path: "/InteractionReview/Archived",
                        render: () => (
                            <AngularBridge
                                name="c-interaction-review-middle-office-reviewed"
                                props={{
                                    componentKey: "interactionReview"
                                }}
                                componentClassName="h-fill"
                            />
                        )
                    }
                ]
            },
            {
                isPermissoned: x =>
                    x.hasPermission(
                        RedDeer.Contracts.Enums.PermissionType.CanAccessMifid
                    ) &&
                    x.hasPermission(
                        RedDeer.Contracts.Enums.PermissionType
                            .CanAccessMifidMeetingReview
                    ),
                title: "Meeting review",
                path: "/Activity/MeetingReview",
                render: () => (
                    <overrides.LegacyGlobal>
                        <AngularBridge
                            name="c-mifid-meeting-review"
                            componentClassName="r-bridge"
                        />
                    </overrides.LegacyGlobal>
                )
            }
        ]
    },
    {
        onlyShowIfThereArePermissionedChildren: true,
        title: "Track consumption",
        description: "View research usage and preview cost forecast",
        useFirstChildForMenus: true,
        wrapChildrenRender: children => (
            <TabbedRoutesPageWrapper tabsTestId="trackConsumptionTabs">
                {children}
            </TabbedRoutesPageWrapper>
        ),
        children: [
            {
                isPermissoned: x =>
                    x.hasPermission(
                        RedDeer.Contracts.Enums.PermissionType.CanAccessMifid
                    ) ||
                    (x.hasPermission(
                        RedDeer.Contracts.Enums.PermissionType
                            .CanAccessSurveillance
                    ) &&
                        x.enableMifidReadAccessForSurveillance),
                title: "Track consumption",
                path: "/AnalystDetail",
                render: () => <TrackConsumptionPage />
            },
            {
                isPermissoned: x =>
                    x.hasPermission(
                        RedDeer.Contracts.Enums.PermissionType.CanAccessMifid
                    ),
                title: "Preview cost",
                path: "/Activity/Preview",
                render: () => (
                    <overrides.LegacyGlobal>
                        <AngularBridge
                            name="c-activity-redux"
                            props={{
                                params: { tab: "Overview" },
                                hideTabs: true
                            }}
                        />
                    </overrides.LegacyGlobal>
                )
            },
            {
                isPermissoned: x =>
                    x.hasPermission(
                        RedDeer.Contracts.Enums.PermissionType.CanManageMifid
                    ) &&
                    x.hasPermission(
                        RedDeer.Contracts.Enums.PermissionType
                            .CanLoadExternalResearchUsage
                    ),
                title: "Research usage upload",
                path: "/UploadManager/:viewMode",
                render: ({ params }) => (
                    <overrides.LegacyGlobal>
                        <AngularBridge
                            name="c-mifid-upload-manager-route"
                            props={params}
                            componentClassName="r-bridge"
                        />
                    </overrides.LegacyGlobal>
                )
            }
        ]
    },
    {
        isPermissoned: x =>
            x.hasPermission(
                RedDeer.Contracts.Enums.PermissionType.CanManageMifid
            ),
        title: "Manage evaluation",
        description:
            "Set up and run regular research evaluations (broker votes)",
        useFirstChildForMenus: true,
        wrapChildrenRender: children => (
            <TabbedRoutesPageWrapper tabsTestId="evaluateResearchTabs">
                {children}
            </TabbedRoutesPageWrapper>
        ),
        children: [
            {
                title: "Manage vote",
                path: "/Payments/ResearchEvaluation/",
                render: () => (
                    <overrides.LegacyGlobal>
                        <AngularBridge
                            name="c-mifid-valuation-workflow"
                            componentClassName="r-bridge"
                        />
                    </overrides.LegacyGlobal>
                )
            },
            {
                title: "Set qualitative factors",
                path: "/Payments/EvaluationFactors/",
                render: () => (
                    <overrides.LegacyGlobal>
                        <AngularBridge
                            name="c-mifid-voting-factors"
                            componentClassName="r-bridge"
                        />
                    </overrides.LegacyGlobal>
                )
            },
            {
                title: "Set discretionary allowances",
                path: "/Payments/DiscretionaryAllowance/",
                render: () => (
                    <overrides.LegacyGlobal>
                        <AngularBridge
                            name="c-mifid-discretionary-allowance"
                            componentClassName="r-bridge"
                        />
                    </overrides.LegacyGlobal>
                )
            }
        ]
    },
    {
        isPermissoned: x =>
            x.hasPermission(
                RedDeer.Contracts.Enums.PermissionType.CanManageMifid
            ),
        title: "Monitor inducement",
        description: "View and act on potential MiFID II inducements",
        useFirstChildForMenus: true,
        wrapChildrenRender: children => (
            <TabbedRoutesPageWrapper tabsTestId="monitorInducementTabs">
                {children}
            </TabbedRoutesPageWrapper>
        ),
        children: [
            {
                title: "Review inducement scan",
                path: "/Inducement",
                pathExact: true,
                render: () => <LazyReviewInducementScan />
            },
            {
                title: "Configure screening",
                path: "/Inducement/Configure",
                render: () => (
                    <overrides.LegacyGlobal>
                        <AngularBridge
                            name="c-mifid-inducement-screener-config"
                            componentClassName="r-bridge"
                        />
                    </overrides.LegacyGlobal>
                )
            },
            {
                title: "Configure filters",
                path: "/Inducement/Filters",
                render: () => (
                    <overrides.LegacyGlobal>
                        <AngularBridge
                            name="c-mifid-inducement-filters"
                            componentClassName="r-bridge"
                        />
                    </overrides.LegacyGlobal>
                )
            }
        ]
    },
    {
        isPermissoned: x =>
            x.hasPermission(
                RedDeer.Contracts.Enums.PermissionType.CanManageMifid
            ),
        title: "Set budgets",
        description: "Set up annual research budget",
        path: "/Payments/Budgets/",
        pathExact: true,
        render: () => (
            <overrides.LegacyGlobal>
                <AngularBridge
                    name="c-mifid-budget"
                    componentClassName="r-bridge"
                />
            </overrides.LegacyGlobal>
        )
    },
    {
        isPermissoned: x =>
            x.hasPermission(
                RedDeer.Contracts.Enums.PermissionType.CanManageMifid
            ),
        title: "Manage providers",
        description: "Create and renew rate cards with research providers",
        useFirstChildForMenus: true,
        wrapChildrenRender: children => (
            <TabbedRoutesPageWrapper tabsTestId="providersTabs">
                {children}
            </TabbedRoutesPageWrapper>
        ),
        children: [
            {
                title: "Providers",
                path: "/Payments/RateCards/",
                render: () => (
                    <overrides.LegacyGlobal>
                        <AngularBridge
                            name="c-mifid-rate-card"
                            componentClassName="r-bridge"
                        />
                    </overrides.LegacyGlobal>
                )
            },
            {
                title: "Internal rates",
                path: "/Payments/InternalRates/",
                render: () => (
                    <overrides.LegacyGlobal>
                        <AngularBridge
                            name="c-mifid-internal-rate-card"
                            componentClassName="r-bridge r-bridge-padding"
                        />
                    </overrides.LegacyGlobal>
                )
            }
        ]
    },
    {
        isPermissoned: x =>
            x.hasPermission(
                RedDeer.Contracts.Enums.PermissionType.CanManageMifid
            ),
        title: "Map organisation",
        description: "Manage organisation hierarchy",
        path: "/Payments/OrganisationChart/",
        render: () => (
            <LegacyGlobal>
                <AngularBridge name="c-mar-organisation-chart-page" />
            </LegacyGlobal>
        )
    },
    {
        onlyShowIfThereArePermissionedChildren: true,
        title: "View reports",
        description: "Create and produce regular reports across the system",
        useFirstChildForMenus: true,
        wrapChildrenRender: children => (
            <TabbedRoutesPageWrapper tabsTestId="reportTabs">
                {children}
            </TabbedRoutesPageWrapper>
        ),
        children: [
            {
                isPermissoned: x =>
                    x.hasPermission(
                        RedDeer.Contracts.Enums.PermissionType
                            .CanAccessCustomReports
                    ),
                title: "View reports",
                path: "/Reports/",
                pathExact: true,
                render: () => (
                    <div className="o-reports">
                        <AngularBridge
                            name="c-reporting-list"
                            props={{ componentKey: "customReports" }}
                        />
                    </div>
                ),
                children: [
                    {
                        path: "/Reports/:id",
                        render: ({ params }) => (
                            <div className="o-reports is-report-view">
                                <AngularBridge
                                    name="c-reporting-view"
                                    props={{
                                        componentKey: "customReports",
                                        params,
                                        isFirst: true
                                    }}
                                />
                            </div>
                        )
                    },
                    {
                        title: "Print report",
                        path: "/PrintReport/:id",
                        isPrintableView: true,
                        render: ({ params }) => (
                            <AngularBridge
                                name="c-print-report-route"
                                props={params}
                            />
                        )
                    },
                    {
                        title: "View report",
                        path: "/ExtReports/:id",
                        render: ({ params }) => (
                            <LegacyGlobal>
                                <AngularBridge
                                    name="c-external-report"
                                    props={params}
                                />
                            </LegacyGlobal>
                        )
                    }
                ]
            },
            {
                isPermissoned: x =>
                    x.hasPermission(
                        RedDeer.Contracts.Enums.PermissionType
                            .CanAccessSurveillance
                    ) &&
                    (x.splits?.UseReactNavigationExportDataPage ?? false),
                title: "Export data",
                path: "/ExportData",
                render: () => <AngularBridge name="c-mar-reporting-page" />
            },
            {
                isPermissoned: x =>
                    x.hasPermission(
                        RedDeer.Contracts.Enums.PermissionType.CanManageMifid
                    ),
                title: "Export data",
                path: "/ExportData",
                render: () => (
                    <overrides.LegacyGlobal>
                        <AngularBridge
                            name="c-mifid-reporting"
                            componentClassName="r-bridge"
                        />
                    </overrides.LegacyGlobal>
                )
            }
        ]
    },
    communityRoute
];

export const settingsRoute: IRoute = {
    onlyShowIfThereArePermissionedChildren: true,
    title: "Settings",
    description: x =>
        x.hasPermission(
            RedDeer.Contracts.Enums.PermissionType.CanAccessSurveillance
        )
            ? "Manage org chart, lexicons, portfolios and other settings"
            : "Manage system settings",
    path: "/Settings",
    render: () => <SettingsPage />,
    wrapChildrenRender: children => (
        <TabbedRoutesPageWrapper tabsTestId="settingTabs">
            {children}
        </TabbedRoutesPageWrapper>
    ),
    children: [
        {
            isPermissoned: x =>
                x.hasPermission(
                    RedDeer.Contracts.Enums.PermissionType.CanAccessSurveillance
                ),
            title: "Organisation chart",
            path: "/Payments/OrganisationChart/",
            render: () => (
                <LegacyGlobal>
                    <AngularBridge name="c-mar-organisation-chart-page" />
                </LegacyGlobal>
            )
        },
        {
            isPermissoned: x =>
                x.hasPermission(
                    RedDeer.Contracts.Enums.PermissionType.CanAccessSurveillance
                ),
            title: "Lexicon management",
            path: "/Lexicon",
            render: () => <LazyLexiconPage />
        },
        {
            isPermissoned: x =>
                x.hasPermission(
                    RedDeer.Contracts.Enums.PermissionType.CanManageSurveillance
                ),
            title: "Case Tag management",
            path: "/CaseTag",
            render: () => <LazyCaseTagPage />
        },
        {
            isPermissoned: x =>
                x.hasPermission(
                    RedDeer.Contracts.Enums.PermissionType.CanManageSurveillance
                ),
            title: "Unusual instruments",
            path: "/UnusualInstruments",
            render: () => <LazyUnusualInstrumentsPage />
        },
        {
            isPermissoned: x =>
                x.splits?.UseReactNavigationInsiderManagementPage ?? false,
            title: "Insider management",
            path: "/Insider",
            render: () => <AngularBridge name="c-mar-insider-page" />
        },
        {
            isPermissoned: x =>
                x.hasPermission(
                    RedDeer.Contracts.Enums.PermissionType.CanAccessSurveillance
                ),
            title: "Portfolio manager",
            path: "/PortfolioManager",
            pathExact: true,
            render: () => (
                <LegacyGlobal>
                    <AngularBridge name="c-portfolio-manager-list" />
                </LegacyGlobal>
            ),
            children: [
                {
                    isPermissoned: x =>
                        x.hasPermission(
                            RedDeer.Contracts.Enums.PermissionType
                                .CanAssignPortfolios
                        ),
                    title: "Assign portfolios",
                    path: "/PortfolioManager/Assign",
                    render: () => (
                        <LegacyGlobal>
                            <AngularBridge name="c-portfolio-manager-assign" />
                        </LegacyGlobal>
                    )
                }
            ]
        },
        {
            isPermissoned: x =>
                x.hasBundle(
                    RedDeer.Contracts.Enums.PermissionBundle.AcquireMedia
                ),
            title: "News store",
            path: "/NewsStore",
            render: () => (
                <LegacyGlobal>
                    <AngularBridge name="c-news-store-route" />
                </LegacyGlobal>
            )
        }
    ]
};

export const adminRoute: IRoute = {
    onlyShowIfThereArePermissionedChildren: true,
    title: "Admin",
    path: "/Admin",
    render: () => <AdminPage />,
    children: [
        {
            isPermissoned: x =>
                x.hasPermission(
                    RedDeer.Contracts.Enums.PermissionType
                        .CanManageConfiguration
                ),
            title: "Configuration",
            path: "/Configuration",
            render: () => (
                <LegacyGlobal>
                    <AngularBridge name="c-configuration-route" />
                </LegacyGlobal>
            )
        },
        {
            isPermissoned: x =>
                x.hasPermission(
                    RedDeer.Contracts.Enums.PermissionType.CanManageDataSources
                ),
            title: "Connections",
            path: "/Connection",
            render: () => (
                <LegacyGlobal>
                    <AngularBridge name="c-connection-route" />
                </LegacyGlobal>
            )
        },
        {
            isPermissoned: x =>
                x.hasPermission(
                    RedDeer.Contracts.Enums.PermissionType.CanManageDataSources
                ),
            title: "Data source",
            path: "/DataSource",
            render: () => (
                <LegacyGlobal>
                    <AngularBridge name="c-data-source-route" />
                </LegacyGlobal>
            )
        },
        {
            isPermissoned: x =>
                x.hasPermission(
                    RedDeer.Contracts.Enums.PermissionType
                        .CanManageConfiguration
                ),
            title: "Delegation manager",
            path: "/DelegationManager",
            render: () => (
                <LegacyGlobal>
                    <AngularBridge name="c-delegation-manager-route" />
                </LegacyGlobal>
            )
        },
        {
            isPermissoned: x =>
                x.hasPermission(
                    RedDeer.Contracts.Enums.PermissionType.CanAccessDemoFeatures
                ),
            title: "Demo console",
            path: "/DemoConsole",
            render: () => <DemoConsolePage />
        },
        {
            isPermissoned: x =>
                x.hasPermission(
                    RedDeer.Contracts.Enums.PermissionType.CanAccessDemoFeatures
                ),
            title: "Demo data",
            path: "/DemoData",
            render: () => <LazyDemoDataPage />
        },
        {
            isPermissoned: x =>
                x.hasPermission(
                    RedDeer.Contracts.Enums.PermissionType
                        .CanManageConfiguration
                ),
            title: "Admin Tools",
            path: "/AdminTools",
            render: () => <LazyAdminToolsPage />
        },
        {
            isPermissoned: x =>
                x.hasPermission(
                    RedDeer.Contracts.Enums.PermissionType
                        .CanManageConfiguration
                ),
            title: "LLM Prompt Templates",
            path: "/LlmPromptTemplate",
            render: () => <LazyLlmPromptTemplatePage />
        },
        {
            isPermissoned: x =>
                x.hasPermission(
                    RedDeer.Contracts.Enums.PermissionType.CanManageUsers
                ),
            title: "Group manager",
            path: "/UserGroupManager",
            render: () => (
                <LegacyGlobal>
                    <AngularBridge name="c-user-group-manager-route" />
                </LegacyGlobal>
            )
        },
        {
            isPermissoned: x =>
                x.hasPermission(
                    RedDeer.Contracts.Enums.PermissionType.CanRunLoadTests
                ),
            title: "Load test",
            path: "/LoadTest",
            render: () => (
                <LegacyGlobal>
                    <AngularBridge name="c-load-test-route" />
                </LegacyGlobal>
            )
        },
        {
            isPermissoned: x =>
                x.hasPermission(
                    RedDeer.Contracts.Enums.PermissionType
                        .CanManageNonDisplayAgreements
                ),
            title: "Non-display agreements",
            path: "/NonDisplayAgreement",
            render: () => (
                <LegacyGlobal>
                    <AngularBridge name="c-non-display-agreement-route" />
                </LegacyGlobal>
            )
        },
        {
            isPermissoned: x =>
                x.hasPermission(
                    RedDeer.Contracts.Enums.PermissionType
                        .CanManageRestrictedStocks
                ),
            title: "Restricted stocks",
            path: "/RestrictedStock",
            render: () => (
                <LegacyGlobal>
                    <AngularBridge name="c-restricted-stock-route" />
                </LegacyGlobal>
            )
        },
        {
            isPermissoned: x =>
                x.hasPermission(
                    RedDeer.Contracts.Enums.PermissionType
                        .CanManageConfiguration
                ),
            title: "Test area",
            path: "/TestArea",
            render: () => <LazyTestAreaPage />
        },
        {
            isPermissoned: x =>
                x.hasPermission(
                    RedDeer.Contracts.Enums.PermissionType
                        .CanManageConfiguration
                ),
            title: "System monitor",
            path: "/SystemMonitor",
            render: () => <LazySystemMonitorPage />
        },
        {
            isPermissoned: x =>
                x.hasPermission(
                    RedDeer.Contracts.Enums.PermissionType
                        .CanMangeTradeableUniverse
                ),
            title: "Tradeable universe",
            path: "/TradableUniverse",
            render: () => (
                <LegacyGlobal>
                    <AngularBridge name="c-tradable-universe-route" />
                </LegacyGlobal>
            )
        },
        {
            isPermissoned: x =>
                x.hasPermission(
                    RedDeer.Contracts.Enums.PermissionType.CanManageUsers
                ),
            title: "Users",
            path: "/Users",
            pathExact: true,
            render: () => (
                <LegacyGlobal>
                    <AngularBridge name="c-user-management-route" />
                </LegacyGlobal>
            ),
            children: [
                {
                    title: "Edit user",
                    path: "/Users/Edit/:id",
                    render: ({ params }) => (
                        <LegacyGlobal>
                            <AngularBridge
                                name="c-user-management-edit-route"
                                props={params}
                            />
                        </LegacyGlobal>
                    )
                },
                {
                    title: "Edit permissions",
                    path: "/Users/EditPermissions/:id",
                    render: ({ params }) => (
                        <LegacyGlobal>
                            <AngularBridge
                                name="c-user-management-edit-permissions-route"
                                props={params}
                            />
                        </LegacyGlobal>
                    )
                }
            ]
        }
    ]
};

export const userRoute: IRoute = {
    isPermissoned: () => true,
    title: "User",
    path: "/User",
    render: () => <UserPage />,
    children: [
        {
            title: "My profile",
            path: "/MyProfile",
            render: () => (
                <LegacyGlobal>
                    <AngularBridge
                        name="c-my-profile-route"
                        props={{ isReactNav: true }}
                    />
                </LegacyGlobal>
            )
        },
        {
            isPermissoned: x =>
                x.hasPermission(
                    RedDeer.Contracts.Enums.PermissionType.CanImpersonateUsers
                ) && !x.isImpersonating,
            title: "Impersonate user",
            path: "/Impersonation",
            render: () => (
                <LegacyGlobal>
                    <AngularBridge name="c-impersonation-route" />
                </LegacyGlobal>
            )
        },
        {
            isPermissoned: x => x.isImpersonating,
            title: "Stop impersonating",
            path: "/StopImpersonating",
            render: () => <StopImpersonatingPage />
        }
    ]
};

export const appendRoutes: IRoute[] = [
    {
        isPermissoned: () => true,
        title: "Home",
        path: "/",
        pathExact: true,
        render: () => <HomePage />
    },

    {
        // debug features only accessible by manually entering url
        isPermissoned: () => true,
        title: "Debug features",
        path: "/Feature",
        render: () => (
            <LegacyGlobal>
                <AngularBridge name="c-feature-route" />
            </LegacyGlobal>
        )
    },

    {
        // developer example pages only accessible by manually entering urls
        isPermissoned: _ => true,
        wrapChildrenRender: children => (
            <TabbedRoutesPageWrapper tabsTestId="settingTabs">
                {children}
            </TabbedRoutesPageWrapper>
        ),
        children: [
            {
                isPermissoned: _ => true,
                title: "Table",
                path: "/Examples/Table",
                render: () => <LazyTableExamplePage />
            },
            {
                isPermissoned: _ => true,
                title: "Infinite Table",
                path: "/Examples/InfiniteTable",
                render: () => <LazyInfiniteTableExamplePage />
            },
            {
                isPermissoned: _ => true,
                title: "VirtualScroll",
                path: "/Examples/VirtualScroll",
                render: () => <LazyVirtualScrollExamplePage />
            }
        ]
    },

    {
        isPermissoned: x =>
            x.hasPermission(
                RedDeer.Contracts.Enums.PermissionType.CanManageSurveillance
            ) ||
            x.hasPermission(
                RedDeer.Contracts.Enums.PermissionType.CanManageConfiguration
            ),
        title: "Slack Connected Surveillance App",
        path: "/SlackConnectedSurveillanceApp",
        render: () => <LazySlackConnectedSurveillanceAppPage />
    },
    {
        isPermissoned: x =>
            x.hasPermission(
                RedDeer.Contracts.Enums.PermissionType.CanManageSurveillance
            ) ||
            x.hasPermission(
                RedDeer.Contracts.Enums.PermissionType.CanManageConfiguration
            ),
        title: "Microsoft Graph Connected Surveillance App",
        path: "/MicrosoftGraphConnectedSurveillanceApp",
        render: () => <LazyMicrosoftGraphConnectedSurveillanceAppPage />
    },
    {
        isPermissoned: x =>
            x.hasPermission(
                RedDeer.Contracts.Enums.PermissionType.CanManageSurveillance
            ) ||
            x.hasPermission(
                RedDeer.Contracts.Enums.PermissionType.CanManageConfiguration
            ),
        title: "Microsoft Graph Exchange Connected Surveillance App",
        path: "/MicrosoftGraphExchangeConnectedSurveillanceApp",
        render: () => <LazyMicrosoftGraphExchangeConnectedSurveillanceAppPage />
    },
    {
        isPermissoned: x =>
            x.hasPermission(
                RedDeer.Contracts.Enums.PermissionType.CanManageSurveillance
            ) ||
            x.hasPermission(
                RedDeer.Contracts.Enums.PermissionType.CanManageConfiguration
            ),
        title: "Client Agent Connected Surveillance App",
        path: "/ClientAgentConnectedSurveillanceApp",
        render: () => <LazyClientAgentConnectedSurveillanceAppPage />
    },
    {
        // not found page (needs to be at the end of this list)
        isPermissoned: () => true,
        title: "Not found",
        path: "/",
        render: () => <HomePage />
    }
    /* IMPORTANT not found page needs to be at the very end of this list */
];
